// src/pages/DataRequest.jsx
// Data Request — Creates a SIM-T ticket

import React, { useState, useEffect } from 'react';

const toolOptions = [
    'Tenant 178', 'Tenant 290', 'Tenant 310', 'Tenant 321', 'Tenant 333', 'Tenant 398', 'Additional Tools', 'Other'
];

const DataRequest = ({ userAlias }) => {
    const [title, setTitle] = useState('Permission Data Request - (Insert Tenant 178/290/310/321/333/398/Additional Tools)');
    const [orgTeamName, setOrgTeamName] = useState('');
    const [tool, setTool] = useState('');
    const [condition, setCondition] = useState('');
    const [summary, setSummary] = useState('');
    const [existsElsewhere, setExistsElsewhere] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitError, setSubmitError] = useState('');

    const isValid = title.trim() && tool;

    // Auth check on load
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const { getAccessToken, refreshToken } = await import('../services/auth');
                const token = getAccessToken();
                if (!token && refreshToken) await refreshToken();
            } catch (e) {}
        };
        checkAuth();
    }, []);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const { getAccessToken } = await import('../services/auth');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
            const token = getAccessToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const descBody = [
                `## Data Request`,
                ``,
                `**Requester:** ${userAlias}`,
                `**Org/Team:** ${orgTeamName || 'N/A'}`,
                `**Tool:** ${tool}`,
                ``,
                `---`,
                `### Condition for Data Request`,
                condition || 'N/A',
                ``,
                `### Summary / Scope / Impact`,
                summary || 'N/A',
                ``,
                `### Does this info/data exist in other dashboards?`,
                existsElsewhere || 'N/A',
            ].join('\n');

            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    operation: 'createTicket',
                    title: `[Data Request] ${title}`,
                    description: descBody,
                    severity: 'SEV_5',
                    requesterAlias: userAlias
                })
            });

            const raw = await response.json();
            const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;

            if (response.status === 401) { sessionStorage.clear(); window.location.href = '/'; return; }
            if (!response.ok || result.error) throw new Error(result.error || `API error: ${response.status}`);

            setSubmitSuccess(result);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <div style={{ maxWidth: '700px', margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ color: '#166534', marginBottom: '8px' }}>Data Request Submitted!</h2>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>Ticket ID: <strong>{submitSuccess.ticketId}</strong></p>
                {submitSuccess.ticketUrl && (
                    <a href={submitSuccess.ticketUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '8px', background: '#0073bb', color: '#fff', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                        View Ticket on SIM-T ↗
                    </a>
                )}
                <div style={{ marginTop: '20px' }}>
                    <button onClick={() => window.location.href = '/'} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>← Back to Home</button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '4px' }}>📊 Data Request</h1>

            {/* Info Section */}
            <div style={{ padding: '16px', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '24px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 10px' }}>Data Request in the context of Permissions & Security team management typically refers to the process of gathering and providing user access information.</p>

                <p style={{ fontWeight: '700', margin: '10px 0 6px' }}>Purpose of Data Request:</p>
                <ul style={{ margin: '0 0 10px', paddingLeft: '20px' }}>
                    <li>To provide stakeholders visibility into user access patterns</li>
                    <li>To maintain compliance and audit requirements</li>
                    <li>To support access review processes</li>
                    <li>To assist in security governance</li>
                </ul>

                <p style={{ fontWeight: '700', margin: '10px 0 6px' }}>Key Components of a Data Request:</p>
                <ul style={{ margin: '0 0 10px', paddingLeft: '20px' }}>
                    <li>Username/Employee ID</li>
                    <li>Full Name</li>
                    <li>Department/Business Unit</li>
                    <li>Role/Position</li>
                    <li>Access Level/Permissions</li>
                    <li>Date Access Granted</li>
                    <li>Last Access Date</li>
                    <li>Access Status (Active/Inactive)</li>
                </ul>

                <p style={{ margin: '0', fontSize: '12px', color: '#92400e' }}>⭐ <strong>Note: The first response SLA for this SIM is 5 business days.</strong></p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>If you don't hear from us within the SLA please drop an email to: ctps-permissions-security@amazon.com</p>
            </div>

            {submitError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>❌ {submitError}</div>}

            {/* Title */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Please provide a title (Permission Data Request - Insert Tenant) <span style={{ color: '#d13212' }}>*</span></label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Org/Team */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Please provide your Org/Team Name:</label>
                <textarea value={orgTeamName} onChange={(e) => setOrgTeamName(e.target.value)} rows={2}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Tool */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Please specify the tool for which data is needed (select only that apply) <span style={{ color: '#d13212' }}>*</span></label>
                <select value={tool} onChange={(e) => setTool(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                    <option value="">— Select —</option>
                    {toolOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {/* Condition */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Please specify a condition for the data request (based on reports of, cost centre, etc):</label>
                <textarea value={condition} onChange={(e) => setCondition(e.target.value)} rows={2}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Summary */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Please provide a summary of your request and the scope/impact of the data requested:</label>
                <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Exists elsewhere */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Does this info/data exist in other dashboards?</label>
                <textarea value={existsElsewhere} onChange={(e) => setExistsElsewhere(e.target.value)} rows={2}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={!isValid || isSubmitting}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: isValid && !isSubmitting ? 'pointer' : 'not-allowed', border: 'none', background: isValid ? '#f59e0b' : '#ccc', color: isValid ? '#000' : '#fff' }}>
                {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Data Request'}
            </button>
        </div>
    );
};

export default DataRequest;
